
import type {
  ApiError,
  ConflictApiError,
  Identifiable,
  QueryRanking,
  QueryRankingConfigDTO,
  QueryRankingElementRanking,
  QueryRankingElementRankingDTO,
  QueryRankingHiddenElementDTO,
  QueryRankingPinnedElementDTO,
  QueryRankingToolConfigProvider,
} from '@empathyco/playboard-core-domain'
import type { Component, ComputedRef, Ref, SetupContext } from 'vue'
import type { EpbSelectOption } from '../../components/shared/epb-select'
import type { ToolingFormLayoutSection } from '../../components/tooling/tooling-form-layout.component.vue'
import type {
  RegularFilterSelectedValues,
  ResolveConflictOption,
  ToolsMessages,
} from '../../models'
import type { QueryRankingDomainService } from '../../services/interfaces'
import { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'
import { computed, defineComponent, getCurrentInstance, ref, watch } from 'vue'
import AttributesRankingList from '../../components/configurations/attributes-ranking-list.component.vue'
import HiddenAttributesList from '../../components/configurations/hidden-attributes-list.component.vue'
import HiddenProductsList from '../../components/configurations/hidden-products-list.component.vue'
import PinnedProductsList from '../../components/configurations/pinned-products-list.component.vue'
import ProductsRankingList from '../../components/configurations/products-ranking-list.component.vue'
import ConflictErrorModalWithOptions from '../../components/crud/conflict-error-modal/conflict-error-modal-with-options.component.vue'
import { UiConfirm } from '../../components/keen-ui'
import AttributesQueryRankingModal from '../../components/search-query-ranking/attributes-query-ranking-modal.vue'
import HiddenAttributesQueryRankingModal from '../../components/search-query-ranking/hidden-attributes-query-ranking-modal.vue'
import HiddenProductsQueryRankingModal from '../../components/search-query-ranking/hidden-products-query-ranking-modal.vue'
import PinnedProductQueryRankingModal from '../../components/search-query-ranking/pinned-product-query-ranking-modal.vue'
import ProductsQueryRankingModal from '../../components/search-query-ranking/products-query-ranking-modal.vue'
import QueryRankingSettingsSection from '../../components/search-query-ranking/query-ranking-settings-section.vue'
import ResultCardQueryRanking from '../../components/search-query-ranking/result-card-query-ranking.component.vue'
import ToolingPreviewQueryRanking from '../../components/search-query-ranking/tooling-preview-query-ranking.component.vue'
import { EpbKeywords } from '../../components/shared/epb-keywords'
import { EpbSelect } from '../../components/shared/epb-select'
import { EpbSwitch } from '../../components/shared/epb-switch'
import { EpbText } from '../../components/shared/epb-text'
import { EpbTextbox } from '../../components/shared/epb-textbox'
import FieldValueSelect from '../../components/shared/field-value-select.component.vue'
import ToolingFormLayout from '../../components/tooling/tooling-form-layout.component.vue'
import ToolingRestrictedToggle from '../../components/tooling/tooling-restricted-toggle.component.vue'
import { useContextPrefill, useContextPrefillSource } from '../../composables'
import { useApiError } from '../../composables/use-api-error'
import { useEndpoints } from '../../composables/use-endpoints'
import { useToolingForm } from '../../composables/use-tooling-form'
import { useToolingRestricted } from '../../composables/use-tooling-restricted'
import { useUpdateItemWithContext } from '../../composables/use-update-item-with-context'
import { CONTAINER } from '../../config/inversify.config'
import { TYPES } from '../../config/inversify.types'
import { ConfigurationToolIdEnum, EndpointContextEnum, FeatureIdEnum } from '../../enums'
import { ROUTER_CONSTS } from '../../router/router.consts'
import { ConfigurationsUtils } from '../../utils/configurations.utils'

export interface SettingsSection {
  icon: string
  listComponent: Component
  modalComponent: Component
  messages:
    | ToolsMessages['configurations']['fields']['attributes']
    | ToolsMessages['configurations']['fields']['hiddenAttributes']
    | ToolsMessages['configurations']['fields']['products']
    | ToolsMessages['configurations']['fields']['pinnedProducts']
    | ToolsMessages['configurations']['fields']['hiddenProducts']
  selectedQueryRankingConfig: Ref<QueryRankingConfigDTO>
  queryRankingConfigs: ComputedRef<Array<QueryRankingConfigDTO>>
  updateQueryRankingConfigs: (queryRankingConfigs: Array<QueryRankingConfigDTO>) => void
}

export default defineComponent({
  name: 'SearchQueryRankingForm',
  components: {
    ConflictErrorModalWithOptions,
    EpbKeywords,
    EpbSelect,
    EpbSwitch,
    EpbText,
    EpbTextbox,
    FieldValueSelect,
    QueryRankingSettingsSection,
    ResultCardQueryRanking,
    ToolingFormLayout,
    ToolingPreviewQueryRanking,
    ToolingRestrictedToggle,
    UiConfirm,
  },
  setup(props, ctx: SetupContext) {
    const {
      proxy: { $epb, $route, $refs },
    } = getCurrentInstance()
    const messages: ToolsMessages['configurations'] = $epb.messages.tools.configurations
    const queryRankingUpdateRoute =
      ROUTER_CONSTS.routes[FeatureIdEnum.CONFIGURATION][DomainEnum.SEARCH].update
    const updateMode = $route.name === queryRankingUpdateRoute

    const {
      selectedFiltersValues,
      endpointId: contextEndpointId,
      contextBrowseField: contextAttributeOption,
      contextBrowseValue: contextFacetSelected,
    } = useContextPrefillSource()

    const isCreateMode = computed(() => !updateMode && !$route.params.id)

    const domainService = CONTAINER.get<QueryRankingDomainService>(TYPES.QueryRankingDomainService)
    const toolConfig = CONTAINER.get<QueryRankingToolConfigProvider>(
      TYPES.QueryRankingToolConfigProvider,
    )()

    const { getErrorMessage, isConflictError } = useApiError()
    const { isRestricted } = useToolingRestricted()

    const { endpoints } = useEndpoints()

    const toolingForm = useToolingForm<QueryRanking>(props, ctx, { messages, updateMode })
    const { updateItemWithContext } = useUpdateItemWithContext<QueryRanking>()

    const inheritance = ref(toolingForm.item.value.criteria.inheritance ?? false)

    toolingForm.initItem({ forceFetch: true }).finally(() => {
      const isCreationMode = !updateMode
      if (isCreationMode && !toolingForm.isCloneMode()) {
        updateItemWithContext(toolingForm.item)
      }
      inheritance.value = toolingForm.item.value.criteria.inheritance ?? false
    })

    const resolveConflictModal = ref()
    const saveConflictApiError = ref<ConflictApiError>(undefined)

    const confirmEndpointIdChangeDialog = ref()

    const endpointId = ref<QueryRanking['endpointId']>()

    const context = computed(() => {
      const contextString =
        endpoints.value.find(endpoint => endpoint.value === endpointId.value)?.context ?? 'query'
      return (
        Object.values(EndpointContextEnum).find(value => value === contextString) ??
        EndpointContextEnum.QUERY
      )
    })

    const isReadOnly = computed<boolean>(
      () => toolingForm.readonly.value || isRestricted(toolingForm.item.value.restrictedRoles),
    )

    const disabledSections = computed<Array<ToolingFormLayoutSection>>(() => {
      if (isReadOnly.value) {
        return ['details', 'filters']
      }

      return toolingForm.item.value.title &&
        (toolingForm.item.value.criteria.queries.length ||
          toolingForm.item.value.criteria.browseValue)
        ? []
        : ['settings', 'preview']
    })

    const browseFieldOptions = computed<Array<EpbSelectOption>>(
      () =>
        toolConfig.browseFields?.map(browseField => ({
          value: browseField.id,
          label: browseField.displayName,
          isCategory: browseField.isCategory,
        })) ?? [],
    )

    const onSetEnabled = (value: boolean): void => {
      toolingForm.item.value.criteria.enabled = value
    }

    const onRestrictedToggleChange = (value: QueryRanking['restrictedRoles']): void => {
      toolingForm.item.value.restrictedRoles = value
    }

    const onSelectAllChildChange = (value: boolean): void => {
      toolingForm.item.value.criteria.inheritance = value
      inheritance.value = value
    }

    function setEndpointId(value: QueryRanking['endpointId']) {
      const newEndpointContext =
        endpoints.value.find(endpoint => endpoint.value === value)?.context ??
        EndpointContextEnum.QUERY
      const currentContext = context.value

      if (
        toolingForm.item.value.endpointId &&
        newEndpointContext !== currentContext &&
        (!!toolingForm.item.value.criteria.queries.length ||
          !!toolingForm.item.value.criteria.browseField ||
          !toolingForm.item.value.empty)
      ) {
        endpointId.value = value
        confirmEndpointIdChangeDialog.value?.open()
      } else {
        toolingForm.item.value.endpointId = value
      }
    }

    function confirmEndpointIdChange() {
      toolingForm.item.value.endpointId = endpointId.value
      toolingForm.item.value.criteria.queries = []
      toolingForm.item.value.criteria.browseField = undefined
      toolingForm.item.value.criteria.browseValue = undefined
      toolingForm.item.value.productsRanking = []
      toolingForm.item.value.attributesRanking = []
      toolingForm.item.value.hiddenAttributes = []
      toolingForm.item.value.pinnedProducts = []
      toolingForm.item.value.criteria.inheritance = inheritance.value
    }

    const onPreviewFiltersSelectedValuesChange = (
      newPreviewFiltersSelectedValues: Record<string, RegularFilterSelectedValues>,
    ): void => {
      toolingForm.previewFiltersSelectedValues.value = newPreviewFiltersSelectedValues
    }

    const configurationToolSettings = computed<
      Partial<Record<ConfigurationToolIdEnum, SettingsSection>>
    >(() => {
      return {
        [ConfigurationToolIdEnum.ATTRIBUTES_RANKING]: {
          icon: 'attributes',
          listComponent: AttributesRankingList,
          modalComponent: AttributesQueryRankingModal,
          messages: messages.fields.attributes,
          selectedQueryRankingConfig: ref(undefined),
          queryRankingConfigs: computed(() => toolingForm.item.value.attributesRanking),
          updateQueryRankingConfigs: (
            queryRankingConfigs: Array<QueryRankingElementRankingDTO>,
          ): void => {
            toolingForm.item.value.attributesRanking =
              queryRankingConfigs as Array<QueryRankingElementRanking>
          },
        },
        [ConfigurationToolIdEnum.HIDDEN_ATTRIBUTES]: {
          icon: 'hide',
          listComponent: HiddenAttributesList,
          modalComponent: HiddenAttributesQueryRankingModal,
          messages: messages.fields.hiddenAttributes,
          selectedQueryRankingConfig: ref(undefined),
          queryRankingConfigs: computed(() => toolingForm.item.value.hiddenAttributes),
          updateQueryRankingConfigs: (
            queryRankingConfigs: Array<QueryRankingHiddenElementDTO>,
          ): void => {
            toolingForm.item.value.hiddenAttributes = []
            queryRankingConfigs.forEach(config => {
              toolingForm.item.value.addHiddenElement(config)
            })
          },
        },
        [ConfigurationToolIdEnum.PRODUCTS_RANKING]: {
          icon: 'boostbury',
          listComponent: ProductsRankingList,
          modalComponent: ProductsQueryRankingModal,
          messages: messages.fields.products,
          selectedQueryRankingConfig: ref(undefined),
          queryRankingConfigs: computed(() => toolingForm.item.value.productsRanking),
          updateQueryRankingConfigs: (
            queryRankingConfigs: Array<QueryRankingElementRankingDTO>,
          ): void => {
            toolingForm.item.value.productsRanking =
              queryRankingConfigs as Array<QueryRankingElementRanking>
          },
        },
        [ConfigurationToolIdEnum.PINNED_PRODUCTS]: {
          icon: 'pin',
          listComponent: PinnedProductsList,
          modalComponent: PinnedProductQueryRankingModal,
          messages: messages.fields.pinnedProducts,
          selectedQueryRankingConfig: ref(undefined),
          queryRankingConfigs: computed(() => toolingForm.item.value.pinnedProducts),
          updateQueryRankingConfigs: (
            queryRankingConfigs: Array<QueryRankingPinnedElementDTO>,
          ): void => {
            toolingForm.item.value.pinnedProducts = queryRankingConfigs
          },
        },
        [ConfigurationToolIdEnum.HIDDEN_PRODUCTS]: {
          icon: 'hide',
          listComponent: HiddenProductsList,
          modalComponent: HiddenProductsQueryRankingModal,
          messages: messages.fields.hiddenProducts,
          selectedQueryRankingConfig: ref(undefined),
          queryRankingConfigs: computed(() => toolingForm.item.value.hiddenProducts),
          updateQueryRankingConfigs: (
            queryRankingConfigs: Array<QueryRankingHiddenElementDTO>,
          ): void => {
            toolingForm.item.value.hiddenProducts = []
            queryRankingConfigs.forEach(config => {
              toolingForm.item.value.addHiddenElement(config)
            })
          },
        },
      }
    })

    function onAccept(): void {
      toolingForm.onAccept({ errorHandler })
    }

    function onResolveConflict(resolveConflictOption: ResolveConflictOption): void {
      toolingForm.resolveConflict({
        resolveConflictOption,
        errorHandler,
      })
    }

    function errorHandler(error: ApiError): void {
      if (isConflictError(error)) {
        saveConflictApiError.value = error as ConflictApiError
        resolveConflictModal.value.open()
      } else {
        ctx.emit('showError', getErrorMessage(error))
      }
    }

    function addOrUpdateQueryRankingConfigs(
      configurationToolId: ConfigurationToolIdEnum,
      newQueryRankingConfigs: Array<QueryRankingConfigDTO>,
    ): void {
      const configurationTool = configurationToolSettings.value[configurationToolId]
      const selectedQueryRankingConfig = configurationTool.selectedQueryRankingConfig.value
      const queryRankingConfigs = configurationTool.queryRankingConfigs.value
      const updateQueryRankingConfigs = configurationTool.updateQueryRankingConfigs

      if (selectedQueryRankingConfig && 'id' in selectedQueryRankingConfig) {
        updateQueryRankingConfigs(
          ConfigurationsUtils.updateQueryRankingConfig(
            queryRankingConfigs,
            selectedQueryRankingConfig as QueryRankingConfigDTO & Identifiable,
            newQueryRankingConfigs[0],
          ),
        )
      } else {
        const conflictingItem = newQueryRankingConfigs.find(product =>
          domainService.areProductRankingFieldsInConflict(toolingForm.item.value, {
            ...product,
            id: product.fieldValue,
          }),
        )

        if (conflictingItem) {
          openModal(configurationToolId, conflictingItem)
        } else {
          updateQueryRankingConfigs(
            ConfigurationsUtils.addQueryRankingConfigs(queryRankingConfigs, newQueryRankingConfigs),
          )
        }
      }
    }

    function onQueryRankingConfigModalClose(configurationToolId: ConfigurationToolIdEnum): void {
      const configurationTool = configurationToolSettings.value[configurationToolId]
      configurationTool.selectedQueryRankingConfig.value = undefined
    }

    function replaceQueryRankingConfigs(
      configurationToolId: ConfigurationToolIdEnum,
      newQueryRankingConfigs: Array<QueryRankingConfigDTO>,
    ): void {
      const configurationTool = configurationToolSettings.value[configurationToolId]
      const updateQueryRankingConfigs = configurationTool.updateQueryRankingConfigs

      updateQueryRankingConfigs(newQueryRankingConfigs)
    }

    function removeQueryRankingConfig(
      configurationToolId: ConfigurationToolIdEnum,
      fieldValue,
    ): void {
      const configurationTool = configurationToolSettings.value[configurationToolId]
      const queryRankingConfigs = configurationTool.queryRankingConfigs.value
      const updateQueryRankingConfigs = configurationTool.updateQueryRankingConfigs

      updateQueryRankingConfigs(
        ConfigurationsUtils.filterArrayByFieldValue(queryRankingConfigs, fieldValue),
      )
    }

    function openModal(
      configurationToolId: ConfigurationToolIdEnum,
      queryRankingConfig?: QueryRankingConfigDTO,
    ): void {
      configurationToolSettings.value[configurationToolId].selectedQueryRankingConfig.value =
        queryRankingConfig

      const modal = $refs[getModalId(configurationToolId)][0] as unknown as { open: () => void }
      modal.open()
    }

    function getModalId(configurationToolId: ConfigurationToolIdEnum): string {
      return `modal-${configurationToolId}`
    }

    const { setupContextPrefillWatch } = useContextPrefill()

    setupContextPrefillWatch({
      mode: 'browse',
      isCreateMode,
      toolingForm,
      selectedFiltersValues,
      contextAttributeOption,
      contextFacetSelected,
      contextEndpointId,
      endpoints,
      endpointId,
    })

    watch(
      () => toolingForm.item.value.endpointId,
      value => {
        endpointId.value = value
      },
      { immediate: true },
    )

    watch(
      () => toolingForm.item.value.criteria.inheritance,
      newValue => (inheritance.value = newValue ?? false),
      { immediate: true },
    )

    return {
      ...toolingForm,
      baseClass: 'epb-search-query-ranking-form',
      endpoints,
      context,
      disabledSections,
      isReadOnly,
      messages,
      inheritance,
      queryRankingUpdateRoute,
      resolveConflictModal,
      saveConflictApiError,
      confirmEndpointIdChangeDialog,
      endpointId,
      browseFieldOptions,
      configurationToolSettings,
      updateMode,
      onAccept,
      onSelectAllChildChange,
      onPreviewFiltersSelectedValuesChange,
      onRestrictedToggleChange,
      setEndpointId,
      confirmEndpointIdChange,
      onResolveConflict,
      onSetEnabled,
      getModalId,
      openModal,
      addOrUpdateQueryRankingConfigs,
      replaceQueryRankingConfigs,
      removeQueryRankingConfig,
      onQueryRankingConfigModalClose,
      ConfigurationToolIdEnum,
    }
  },
})
